import { useState, useEffect, useContext, createContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { signIn } from "../services/auth";
import { getUserById } from "../services/users";
import { useDispatch } from "react-redux";
import { displayErrorFeedback } from "../app/feedbackSlice";
import CryptoJS from "crypto-js";

const AuthContext = createContext(null);

export const useAuth = () => {
  return useContext(AuthContext);
};

export function useProviderAuth() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLogged, setIsLogged] = useState(false);
  const [user, setUser] = useState(null);
  const dispatch = useDispatch();

  const handleLogin = async (loginData) => {
    const salt = process.env.BDD_SALT;
    const encryptedPassword = CryptoJS.SHA256(loginData.password + salt);
    const encryptedPasswordHex = encryptedPassword.toString(CryptoJS.enc.Hex);

    try {
      const fetchedUser = await signIn({ ...loginData, password: encryptedPasswordHex });
      const { token, user } = fetchedUser;
      setUser(user);
      localStorage.setItem("dpapp_key", JSON.stringify(token));
      localStorage.setItem("dpapp_user_id", user.id);
      localStorage.setItem("dpapp_user_role", user.role);
      setIsLogged(true);
      navigate("/projects");
      return { isLogged: true };
    } catch (error) {
      localStorage.removeItem("dpapp_key");
      localStorage.removeItem("dpapp_user_id");
      localStorage.removeItem("dpapp_user_role");
      setIsLogged(false);
      setUser(null);
      dispatch(displayErrorFeedback({ message: error.message }));
    }
  };

  const handleLogout = () => {
    navigate("/login");

    setTimeout(() => {
      localStorage.removeItem("dpapp_key");
      localStorage.removeItem("dpapp_user_id");
      localStorage.removeItem("dpapp_user_role");
      setIsLogged(false);
      setUser(null);
    }, 1000);
  };

  const fetchUser = async () => {
    const userId = localStorage.getItem("dpapp_user_id");
    if (userId) {
      try {
        const fetchedUser = await getUserById(userId);
        if (fetchedUser.name) {
          setIsLogged(true);
          setUser(fetchedUser);
          if (!/previews/g.test(location.pathname)) {
            navigate("/projects");
          }
        } else {
          handleLogout();
          dispatch(displayErrorFeedback({ message: "You have been disconnected." }));
        }
      } catch (e) {
        console.error(e);
        handleLogout();
        dispatch(displayErrorFeedback({ message: "You have been disconnected." }));
      }
    }
  };

  const getUserType = () => {
    const userRole = localStorage.getItem("dpapp_user_role");
    if (userRole === "undefined") handleLogout();
    return userRole;
  };

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isLogged,
    getUserType,
    onLogin: handleLogin,
    onLogout: handleLogout,
    user,
  };
}

export function ProvideAuth({ children }) {
  const auth = useProviderAuth();
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}
